import {
	KYCFileType,
	GetInvestmentDetailsRes,
	KycWorkflowSettingResponseDto,
} from '@dealstack/shared/lib/generated';
import { AlertTitle, Alert } from '@dealstack/frontend/shared/components/Alert/Alert';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { KycInstructionBar } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/KycInstructions/KycInstructionBar';
import { ProofFileBox } from '@dealstack/frontend/investor/pages/Investment/common/ProofFileBox/ProofFileBox';
import { ProofFileBoxWrapper } from '@dealstack/frontend/investor/pages/Investment/common/ProofFileBox/styled';
import { Flex, Text } from '@mantine/core';
import styled, { css } from 'styled-components';

import { useInvestmentManagePIVProofController } from './useInvestmentManagePIVProofController';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { CertifiableProofUpload } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProofUpload';
import { ProofCertificationStatus } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/ProofCertificationStatus';
import { isCertificationRequiredFor } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProof.utils';

interface Props {
	className?: string;
	investmentDetails: GetInvestmentDetailsRes | undefined;
	kycWorkflowId: string;
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	kycSetting?: KycWorkflowSettingResponseDto;
}

const InvestmentManagePIVProof = ({
	className,
	investmentDetails,
	kycWorkflowId,
	setIsContinueDisabled,
	kycSetting,
}: Props) => {
	const tInvestor = useTranslationInvestor();

	const isCertifiedCopiesRequested =
		kycSetting?.isProofOfInvestmentVehicleCertificationRequired ?? false;

	const {
		proofFilesDetails,
		uploadFileRequest,
		refetchProofs,
		onDelete,
		onFileDrop,
		moreDocsSettings,
		isNewFileUploaded,
	} = useInvestmentManagePIVProofController({
		setIsContinueDisabled,
		investmentDetails,
		kycWorkflowId,
	});

	return (
		<div className={className}>
			<Text>{tInvestor('investment.join.kyc.piv_proofs.descr')}</Text>

			<Alert state="info" size="s">
				<AlertTitle>{tInvestor('investment.join.kyc.piv_proofs.remember')}</AlertTitle>
			</Alert>

			<Flex
				direction="column"
				gap="0.5rem"
				sx={{
					padding: '0.5rem',
					border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
					borderRadius: '4px',
				}}
			>
				{moreDocsSettings?.areMorePivDocsRequested && !isNewFileUploaded && (
					<Text c="red.6">
						{tInvestor('investment.join.kyc.piv.more_docs_requested', {
							comment: moreDocsSettings.morePivDocsRequestedComment ?? '',
						})}
					</Text>
				)}

				{!!proofFilesDetails?.length && (
					<ListWrapper
						isAnyRejected={proofFilesDetails.some(({ status }) =>
							isKycRejected(status)
						)}
					>
						{proofFilesDetails.map((proofFile) => (
							<ProofFileBox
								key={proofFile.fileId}
								fileName={proofFile.fileName}
								fileId={proofFile.fileId}
								onDelete={onDelete(proofFile.id)}
								date={proofFile.createdAt}
								rejectionComment={proofFile.rejectionComment}
								isRejected={isKycRejected(proofFile.status)}
								certificationSlot={
									<ProofCertificationStatus
										kycFile={proofFile}
										certificationRequired={isCertificationRequiredFor(
											kycSetting,
											KYCFileType.INVESTMENT_VEHICLE
										)}
										isRejected={isKycRejected(proofFile.status)}
										onChanged={refetchProofs}
									/>
								}
							/>
						))}
					</ListWrapper>
				)}

				<CertifiableProofUpload
					certificationRequired={isCertificationRequiredFor(
						kycSetting,
						KYCFileType.INVESTMENT_VEHICLE
					)}
					onDropFile={onFileDrop}
					size="STD"
					loading={uploadFileRequest.isPending}
					multiple
				/>
			</Flex>

			<KycInstructionBar isCertifiedCopiesOfDocumentsRequested={isCertifiedCopiesRequested} />
		</div>
	);
};

const ListWrapper = styled.div<{ isAnyRejected: boolean }>`
	${(props) =>
		props.isAnyRejected
			? css`
					display: flex;
					flex-direction: column;
					gap: 0.5rem;
				`
			: css`
					display: grid;
					grid-template-columns: 1fr 1fr;
					grid-auto-rows: 1fr;
					grid-column-gap: 0.5rem;
					grid-row-gap: 0.5rem;

					${ProofFileBoxWrapper} > div:first-of-type {
						box-sizing: border-box;
						height: 100%;
					}
				`}
`;

const Styled = styled(InvestmentManagePIVProof)`
	max-width: 40rem;
	display: flex;
	flex-direction: column;
	gap: 2rem;

	${Alert} {
		background-color: ${(props) => props.theme.v3.colors.gray[0]};
	}
`;

export { Styled as InvestmentManagePIVProof };
